import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ROTATE_MS = 15000;

const HeroSection = ({ events }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-rotate carousel; timer restarts whenever the slide changes (auto or manual)
  useEffect(() => {
    if (!events || events.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % events.length);
    }, ROTATE_MS);

    return () => clearInterval(interval);
  }, [events, currentIndex]);

  if (!events || events.length === 0) return null;

  const event = events[currentIndex];

  const handleBookNow = (e) => {
    e.stopPropagation();
    navigate(`/events/${event.id}`);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  // Custom date formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return '';

    let date;
    // Handle Firestore Timestamp
    if (dateStr && typeof dateStr.toDate === 'function') {
      date = dateStr.toDate();
    } else {
      date = new Date(dateStr);
    }

    // Check for invalid date
    if (isNaN(date.getTime())) return '';

    const dateOptions = { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' };
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: 'UTC' };

    try {
      return `${date.toLocaleDateString('en-US', dateOptions)} | ${date.toLocaleTimeString('en-US', timeOptions)} Onwards`;
    } catch (e) {
      console.error("Date formatting error:", e);
      return '';
    }
  };

  return (
    <div className="relative w-full max-w-[1200px] mx-auto">
      <div
        className="relative h-[340px] sm:h-[380px] lg:h-[420px] rounded-[20px] overflow-hidden cursor-pointer group"
        onClick={() => navigate(`/events/${event.id}`)}
        style={{ background: '#3d4f6f' }}
      >
        {/* Backgrounds: one layer per event, crossfading + slow Ken Burns zoom */}
        {events.map((ev, index) => (
          <div
            key={ev.id || index}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${ev.poster?.verticalUrl || ev.poster?.horizontalUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
              opacity: index === currentIndex ? 0.5 : 0,
              transition: 'opacity 1000ms ease-in-out',
              animation: 'heroKenBurns 22s ease-in-out infinite alternate',
            }}
          />
        ))}

        {/* Light linear gradient overlay - lets poster show through */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, rgba(45, 55, 80, 0.55) 0%, rgba(45, 55, 80, 0.25) 50%, rgba(45, 55, 80, 0.05) 100%)'
          }}
        />

        {/* Content Container */}
        <div className="relative h-full flex justify-between" style={{ paddingLeft: '32px', paddingRight: '32px' }}>

          {/* Left Content: Text - bottom-left aligned; re-keyed per slide so it animates in */}
          <div key={currentIndex} className="flex flex-col justify-end items-start pb-8 sm:pb-10 z-10" style={{ marginRight: '48px' }}>
            {/* Event Name - Poppins SemiBold 32px, line-height 100%, letter-spacing 1% */}
            <h1
              className="text-white mb-3 fade-in-up"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '32px',
                lineHeight: '100%',
                letterSpacing: '0.01em',
              }}
            >
              {event.name}
            </h1>

            {/* Venue details container - width 696, height 62, gap 2px */}
            <div className="fade-in-up" style={{ maxWidth: '696px', gap: '2px', display: 'flex', flexDirection: 'column', animationDelay: '120ms' }}>
              {/* Date & Time */}
              <p
                className="text-white/90"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '20px',
                  fontWeight: 400,
                  lineHeight: '100%',
                  letterSpacing: '0.01em',
                }}
              >
                {formatDate(event.startTime)}
              </p>

              {/* Venue */}
              <p
                className="text-white/80"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '20px',
                  fontWeight: 400,
                  lineHeight: '100%',
                  letterSpacing: '0.01em',
                }}
              >
                {event.eventLocation?.locationText || 'Venue TBA'}
              </p>
            </div>

            {/* Book Now Button - 106x40, padding 8px 12px, border-radius 8px */}
            <button
              onClick={handleBookNow}
              className="bg-[#17BD3B] hover:bg-[#15a834] text-white transition-all duration-300 mt-5 fade-in-up btn-shine hover:shadow-lg hover:shadow-[#17BD3B]/30"
              style={{
                animationDelay: '220ms',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                minWidth: '96px',
                width: '106px',
                height: '40px',
                paddingTop: '8px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                paddingRight: '12px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Book Now
            </button>
          </div>

          {/* Right Content: Poster Card - no shadow, vertically centered */}
          <div className="hidden lg:flex items-center justify-end z-10 py-6 flex-shrink-0">
            <div
              key={currentIndex}
              className="relative rounded-[16px] overflow-hidden flex-shrink-0 fade-in-up transition-transform duration-500 hover:scale-[1.03]"
              style={{ width: '240px', height: '340px', animationDelay: '100ms' }}
            >
              <img
                src={event.poster?.verticalUrl || event.poster?.horizontalUrl}
                alt={event.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators - active dot fills with progress until the next slide */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginTop: '24px', height: '8px' }}>
        {events.map((_, index) => (
          <div
            key={index}
            onClick={() => handleDotClick(index)}
            className="hover:scale-125"
            style={{
              cursor: 'pointer',
              transition: 'all 0.3s',
              borderRadius: '999px',
              height: '8px',
              width: index === currentIndex ? '33px' : '8px',
              backgroundColor: index === currentIndex ? 'rgba(23,189,59,0.25)' : 'rgba(255,255,255,0.25)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {index === currentIndex && (
              <span
                key={currentIndex}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  borderRadius: '999px',
                  backgroundColor: '#17BD3B',
                  animation: `heroProgress ${ROTATE_MS}ms linear forwards`,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HeroSection;
